import {
  InformationCircleIcon,
  UsersIcon,
  UserGroupIcon,
  CogIcon,
  KeyIcon,
} from '@heroicons/react/solid'
import { useMemo } from 'react'
import { Navigate, Route, Routes, useMatch } from 'react-router-dom'
import { Aside, Main, Steps } from 'src/components'
import OnlineUsers from './OnlineUsers'
import Info from './Info'
import Config from './Config'
import SSO from './SSO'
import SAML from './SAML'

import Salesforce from './Salesforce'
import { useGetOrganization } from './queries'
import Users from './Users'
import ValidateOnlineUsersChart from './OnlineUsersChart'
import SalesforceLogo from './Salesforce/SalesforceLogo'

const routes = [
  {
    path: 'info',
    element: <Info />,
    label: 'Info',
    icon: <InformationCircleIcon />,
  },
  {
    path: 'config',
    element: <Config />,
    label: 'Config',
    icon: <CogIcon />,
  },
  {
    path: 'saml',
    element: <SAML />,
    label: 'SAML',
    icon: <KeyIcon />,
  },
  {
    path: 'sso',
    element: <SSO />,
    label: 'SSO',
    icon: <KeyIcon />,
  },
  {
    path: 'salesforce',
    element: <Salesforce />,
    label: 'Salesforce',
    icon: <SalesforceLogo fill="#94a3b8" />,
  },
  {
    path: 'users',
    element: <Users />,
    label: 'Users',
    icon: <UsersIcon />,
  },
  {
    path: 'online-users',
    element: <OnlineUsers />,
    label: 'Online Users',
    icon: <UserGroupIcon />,
  },
  {
    path: 'online-users-chart',
    element: <ValidateOnlineUsersChart />,
    label: 'Online Users Chart',
    icon: <UserGroupIcon />,
  },
]

type Props = {
  organization: string
  username: string
}

export default function ClientOrganization(props: Props) {
  const match = useMatch('/org/:orgId/:tab')

  const organization = useGetOrganization(
    {
      organization: match?.params.orgId || props.organization,
      username: props.username,
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  )

  const isSAML =
    organization.isSuccess && organization.data?.aws_connect_type === 'SAML'

  const isSalesforce =
    organization.isSuccess &&
    organization.data?.aws_connect_type === 'SALESFORCE'

  const isSSO =
    organization.isSuccess && organization.data?.aws_connect_type === 'SSO'

  const filteredRoutes = useMemo(
    () =>
      routes.filter((item) => {
        switch (item.path) {
          case 'saml':
            return isSAML
          case 'users':
            return isSAML
          case 'salesforce':
            return isSalesforce
          case 'sso':
            return isSSO
          default:
            return true
        }
      }),
    [isSAML, isSSO, isSalesforce],
  )

  const currentIndex =
    match && match.params.tab
      ? filteredRoutes.findIndex((item) => item.path === match.params.tab)
      : -1

  return (
    <>
      <Aside
        title="Provisioning"
        hideExtraRow={currentIndex === -1 || !organization.isSuccess}
        displayHelp
      >
        <Steps
          routes={filteredRoutes.filter(
            (item) => item.path !== 'online-users-chart',
          )}
          currentIndex={currentIndex}
        />
      </Aside>

      <Main>
        <Routes>
          {filteredRoutes.map((item) => (
            <Route key={item.path} path={item.path} element={item.element} />
          ))}
          <Route path="*" element={<Navigate to="./info" replace />} />
        </Routes>
      </Main>
    </>
  )
}
